import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ModalCloseButton from './ui/ModalCloseButton';
import ColorPaletteEditor from './ColorPaletteEditor';
import { getSavedPalette, isValidHex, normalizePalette } from '../utils/colorPalette';

const ColorPaletteEditModal = ({
  isOpen,
  onClose,
  onSave,
  initialPalette = [],
  title = 'Edit colors',
  minColors = 3,
  removeConfirmDescription = 'will be removed from the palette.'
}) => {
  const [editedPalette, setEditedPalette] = useState([]);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setEditedPalette(normalizePalette(initialPalette));
    setPendingDeleteIndex(null);
  }, [isOpen, initialPalette]);

  if (!isOpen) return null;

  const savedCount = editedPalette.filter((color) => isValidHex(color.hex)).length;

  const handleSave = () => {
    onSave(getSavedPalette(editedPalette));
    onClose();
  };

  const removePaletteColor = (index) => {
    if (savedCount <= minColors) return;
    setEditedPalette((prev) => prev.filter((_, idx) => idx !== index));
    setPendingDeleteIndex(null);
  };

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  const modalContent = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 p-5">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="overflow-y-auto p-5 sm:p-6">
          <ColorPaletteEditor
            palette={editedPalette}
            onPaletteChange={setEditedPalette}
            onRequestRemove={setPendingDeleteIndex}
            minColors={minColors}
          />
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50 p-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-5 py-2.5 font-medium text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-xl bg-gradient-to-r from-primary to-primary-dark px-6 py-2.5 font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            Save Changes
          </button>
        </div>

        {pendingDeleteIndex !== null && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/75 p-4 backdrop-blur-sm">
            <div
              className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-lg font-extrabold text-gray-950">Remove this color?</h4>
              <p className="mt-2 text-sm leading-6 text-gray-600">
                {[editedPalette[pendingDeleteIndex]?.name, editedPalette[pendingDeleteIndex]?.hex].filter(Boolean).join(' ') || 'This color'}{' '}
                {removeConfirmDescription}
              </p>
              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setPendingDeleteIndex(null)}
                  className="h-10 rounded-xl bg-gray-100 px-4 text-sm font-bold text-gray-700 transition hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => removePaletteColor(pendingDeleteIndex)}
                  className="h-10 rounded-xl bg-red-600 px-4 text-sm font-bold text-white transition hover:bg-red-700"
                >
                  Remove color
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return portalTarget ? createPortal(modalContent, portalTarget) : modalContent;
};

export default ColorPaletteEditModal;
